import { useMsal } from "@azure/msal-react";
import { useAuthenticatedQuery } from "innovate/api/api";
import Loader from "innovate/components/common/Loader";
import { fixed } from "innovate/utils/Constants";
import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface ApprovalStatusItem {
  name: string;
  status: string;
}

interface ApprovedCountry {
  country_code: string;
  country_name: string;
}

interface PendingRequest {
  id: number;
  country_code: string;
  country_name: string;
  request_reason: string | null;
  requested_at: string;
}

interface UserData {
  username: string;
  email: string;
  role: string;
  approved_countries: ApprovedCountry[];
}

interface ApiResponse {
  approval_status_array: ApprovalStatusItem[];
  user: UserData;
  pending_requests: PendingRequest[];
  length: number;
  approval_status: string;
  success: boolean;
}
export const ApprovalContext = createContext<{
  isApproved: boolean;
  isIBApproved: boolean;
  isIEApproved: boolean;
  isUser: boolean | null;
  userName: string;
  userType: string;
  profilePhoto: string | null;
  approvedCountries: ApprovedCountry[];
  pendingRequests: PendingRequest[];
  setUserName: React.Dispatch<React.SetStateAction<string>>;
  setIsUser: React.Dispatch<React.SetStateAction<boolean | null>>;
  setIsApproved: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
}>({
  isApproved: false,
  isIBApproved: false,
  isIEApproved: false,
  isUser: null,
  userName: "",
  userType: "",
  profilePhoto: null,
  approvedCountries: [],
  pendingRequests: [],
  setUserName: () => {},
  setIsUser: () => {},
  setIsApproved: () => {},
  isLoading: true
});

interface ApprovalProviderProps {
  children: ReactNode;
}

const GRAPH_PHOTO_URL = "https://graph.microsoft.com/v1.0/me/photo/$value";

const ApprovalProvider: React.FC<ApprovalProviderProps> = ({ children }) => {
  const { instance } = useMsal();
  const { data, error, isLoading } = useAuthenticatedQuery<ApiResponse>(
    ["approvalStatus", "approvalStatusData"],
    {
      url: `${fixed}approvals/status`,
      method: "GET",
      headers: { "Content-Type": "application/json" },
    }
  );

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize state from sessionStorage if available
  const [isUser, setIsUser] = useState<boolean | null>(() => {
    const stored = sessionStorage.getItem("isUser");
    return stored ? JSON.parse(stored) : null;
  });
  const [userName, setUserName] = useState<string>(() => {
    return sessionStorage.getItem("userName") || "";
  });
  const [userType, setUserType] = useState<string>(() => {
    return sessionStorage.getItem("userType") || "";
  });

  const [isApproved, setIsApproved] = useState<boolean>(() => {
    const stored = sessionStorage.getItem("isApproved");
    return stored ? JSON.parse(stored) : false;
  });
  const [isIBApproved, setIsIBApproved] = useState<boolean>(() => {
    const stored = sessionStorage.getItem("isIBApproved");
    return stored ? JSON.parse(stored) : false;
  });
  const [isIEApproved, setIsIEApproved] = useState<boolean>(() => {
    const stored = sessionStorage.getItem("isIEApproved");
    return stored ? JSON.parse(stored) : false;
  });
  const [approvedCountries, setApprovedCountries] = useState<ApprovedCountry[]>(() => {
    const stored = sessionStorage.getItem("approvedCountries");
    return stored ? JSON.parse(stored) : [];
  });
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>(() => {
    const stored = sessionStorage.getItem("pendingRequests");
    return stored ? JSON.parse(stored) : [];
  });
  const [apiError, setApiError] = useState<string>("");;
  const [profilePhoto, setProfilePhoto] = useState<string | null>(() =>
    sessionStorage.getItem("profilePhoto") || null
  );

  // Fetch Microsoft Graph profile photo once per session — stored as data URL in sessionStorage
  useEffect(() => {
    if (sessionStorage.getItem("profilePhoto")) return; // already cached

    const fetchPhoto = async () => {
      try {
        const accounts = instance.getAllAccounts();
        if (!accounts.length) return;
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["user.read"],
          account: accounts[0],
        });
        const response = await fetch(GRAPH_PHOTO_URL, {
          headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        });
        if (response.ok) {
          const blob = await response.blob();
          // Convert to data URL so it can be stored in sessionStorage and survives remounts
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            sessionStorage.setItem("profilePhoto", dataUrl);
            setProfilePhoto(dataUrl);
          };
          reader.readAsDataURL(blob);
        }
      } catch {
        // No photo — fallback icon shown in Header
      }
    };
    fetchPhoto();
  }, [instance]);

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("isUser", JSON.stringify(isUser));
  }, [isUser]);

  useEffect(() => {
    sessionStorage.setItem("userName", userName);
  }, [userName]);

  useEffect(() => {
    sessionStorage.setItem("userType", userType);
  }, [userType]);

  useEffect(() => {
    sessionStorage.setItem("isApproved", JSON.stringify(isApproved));
  }, [isApproved]);

  useEffect(() => {
    sessionStorage.setItem("isIBApproved", JSON.stringify(isIBApproved));
  }, [isIBApproved]);

  useEffect(() => {
    sessionStorage.setItem("isIEApproved", JSON.stringify(isIEApproved));
  }, [isIEApproved]);

  useEffect(() => {
    sessionStorage.setItem("approvedCountries", JSON.stringify(approvedCountries));
  }, [approvedCountries]);

  useEffect(() => {
    sessionStorage.setItem("pendingRequests", JSON.stringify(pendingRequests));
  }, [pendingRequests]);

  useEffect(() => {
    if (!data?.success) return;
    if (!data?.data) {
      navigate("/access");
      return;
    }
    if (data?.data?.length === 0) {
      navigate("/access");
      return;
    }
    setIsUser(true);
    setUserType(data.data.user.role); 
    setUserName(data.data.user.username);
    
    // Get approved countries from user data
    const userApprovedCountries = data.data.user.approved_countries || [];
    setApprovedCountries(userApprovedCountries);
    
    // Get pending requests from API response
    const userPendingRequests = data.data.pending_requests || [];
    setPendingRequests(userPendingRequests);
    
    // User has access if they have at least one approved country
    const hasAccess = userApprovedCountries.length > 0;
    setIsApproved(hasAccess);
    setIsIBApproved(hasAccess);
    setIsIEApproved(hasAccess);

    if (!hasAccess) {
      navigate("/access");
    }
  }, [data, location.pathname, navigate]);

  const value = useMemo(
    () => ({
      isApproved,
      setIsApproved,
      isLoading,
      isUser,
      setIsUser,
      userName,
      setUserName,
      apiError,
      setApiError,
      userType,
      isIBApproved,
      isIEApproved,
      approvedCountries,
      pendingRequests,
      profilePhoto,
    }),
    [
      isApproved,
      isLoading,
      isUser,
      userName,
      apiError,
      userType,
      isIBApproved,
      isIEApproved,
      approvedCountries,
      pendingRequests,
      profilePhoto,
    ]
  );

  if (isLoading) return <Loader dH="90vh" dW="20vw" />;
  if (error) return `Error: ${error.message}`;
  if (data && !data.success) return <Loader dH="90vh" dW="20vw" />;

  return (
    <ApprovalContext.Provider value={value}>
      {children}
    </ApprovalContext.Provider>
  );
};

export default ApprovalProvider;
